import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Plus, X } from "lucide-react";
import { api, qs } from "@/lib/api";
import { BRANDS } from "@/lib/brands";
import { toZoneLocalInput } from "@/lib/format";
import { useToast } from "@/components/Toaster";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { Input, Label, Textarea } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { TagChips } from "../email/RuleEditor";
import { KIND_LABEL } from "./EventsPage";

export interface EventRecord {
  id: number;
  brand_id: number;
  product_id: number | null;
  name: string;
  kind: string;
  description: string | null;
  location: string | null;
  time_zone: string;
  status: string;
  demio_event_id: string | null;
  settings: Record<string, unknown>;
}

export interface SessionRecord {
  id: number;
  name: string | null;
  starts_at: string;
  ends_at: string | null;
  location: string | null;
  capacity: number | null;
  demio_session_id?: string | null;
  status: string;
  registered?: number;
}

/** Zones the classes have actually used; anything else an event already has is added to the list. */
const ZONES: [string, string][] = [
  ["America/Denver", "Mountain"],
  ["America/Phoenix", "Arizona"],
  ["America/Chicago", "Central"],
  ["America/New_York", "Eastern"],
  ["America/Los_Angeles", "Pacific"],
];

const EVENT_STATUS: [string, string][] = [
  ["scheduled", "Scheduled (open for registration)"],
  ["draft", "Draft (not shown anywhere yet)"],
  ["completed", "Completed"],
  ["cancelled", "Cancelled"],
];

interface DateRow {
  name: string;
  starts: string;
  ends: string;
  location: string;
  capacity: string;
  demio: string;
}
const emptyDate = (): DateRow => ({ name: "", starts: "", ends: "", location: "", capacity: "", demio: "" });

const ids = (v: unknown): number[] => (Array.isArray(v) ? v.map(Number).filter((n) => Number.isFinite(n)) : []);

/** Create an event (with its first dates) or edit one. Settings it doesn't show are kept as they are. */
export function EventDialog({ event, onClose }: { event?: EventRecord; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const editing = !!event;
  const [brand, setBrand] = useState<number>(event?.brand_id ?? 2);
  const [name, setName] = useState(event?.name ?? "");
  const [kind, setKind] = useState(event?.kind ?? "boot_camp");
  const [location, setLocation] = useState(event?.location ?? "");
  const [zone, setZone] = useState(event?.time_zone ?? "America/Denver");
  const [status, setStatus] = useState(event?.status ?? "scheduled");
  const [description, setDescription] = useState(event?.description ?? "");
  const [product, setProduct] = useState<number | "">(event?.product_id ?? "");
  const [demio, setDemio] = useState(event?.demio_event_id ?? "");
  const s = event?.settings ?? {};
  const [regTags, setRegTags] = useState<number[]>(ids(s.registration_tag_ids));
  const [registeredTags, setRegisteredTags] = useState<number[]>(ids(s.registered_tag_ids));
  const [attendedTags, setAttendedTags] = useState<number[]>(ids(s.attended_tag_ids));
  const [noWaitlist, setNoWaitlist] = useState(!!s.no_waitlist);
  const [dates, setDates] = useState<DateRow[]>(editing ? [] : [emptyDate()]);
  const products = useQuery({
    queryKey: ["products", "for-events", brand],
    queryFn: () => api<{ products: { id: number; name: string; brand_id: number }[] }>("/api/v1/products" + qs({ brand_id: brand, active_only: true, limit: 200 })),
    retry: false,
  });
  const zones = ZONES.some(([z]) => z === zone) ? ZONES : [...ZONES, [zone, zone] as [string, string]];
  const webinar = kind === "webinar";

  const settings = () => {
    const out: Record<string, unknown> = { ...s };
    const put = (k: string, v: unknown, keep: boolean) => (keep ? (out[k] = v) : delete out[k]);
    put("registration_tag_ids", regTags, regTags.length > 0);
    put("registered_tag_ids", registeredTags, registeredTags.length > 0);
    put("attended_tag_ids", attendedTags, attendedTags.length > 0);
    put("no_waitlist", true, noWaitlist);
    return out;
  };
  const save = useMutation({
    mutationFn: async () => {
      if (editing) {
        return api(`/api/v1/events/${event.id}`, {
          method: "PATCH",
          json: {
            name,
            kind,
            description,
            location,
            time_zone: zone,
            status,
            product_id: product === "" ? 0 : product,
            demio_event_id: demio,
            settings: settings(),
          },
        });
      }
      const sessions = dates
        .filter((d) => d.starts)
        .map((d) => ({
          name: d.name || undefined,
          starts_at: d.starts,
          ends_at: d.ends || undefined,
          location: d.location || undefined,
          capacity: d.capacity ? Number(d.capacity) : undefined,
          demio_session_id: webinar && d.demio ? d.demio : undefined,
        }));
      const st = settings();
      return api<{ event?: { id: number }; id?: number }>("/api/v1/events", {
        json: {
          brand_id: brand,
          name,
          kind,
          description: description || undefined,
          location: location || undefined,
          time_zone: zone,
          status,
          product_id: product || undefined,
          demio_event_id: webinar && demio ? demio : undefined,
          settings: Object.keys(st).length ? st : undefined,
          sessions: sessions.length ? sessions : undefined,
        },
      });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["events"] });
      if (editing) {
        void qc.invalidateQueries({ queryKey: ["event", event.id] });
        toast(`Saved ${name}`);
        onClose();
        return;
      }
      const res = r as { event?: { id: number }; id?: number };
      const id = res.event?.id ?? res.id;
      toast(`Created ${name}`);
      onClose();
      if (id) void navigate({ to: "/events/$eventId", params: { eventId: String(id) } });
    },
    onError: (e) => toast(e.message, "error"),
  });
  const setDate = (i: number, patch: Partial<DateRow>) => setDates(dates.map((d, j) => (j === i ? { ...d, ...patch } : d)));
  const badDates = dates.some((d) => d.ends && d.starts && d.ends < d.starts);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] w-[min(720px,calc(100vw-2rem))] overflow-y-auto">
        <DialogTitle>{editing ? `Edit ${event.name}` : "New event"}</DialogTitle>
        <DialogDescription>
          {editing
            ? "Changes show on the roster and the contact screens right away. Dates are edited one at a time on the event page."
            : "A Boot Camp, webinar or class, with its dates. People register on the event page, at checkout, or through Claude."}
        </DialogDescription>
        <div className="space-y-4 text-sm">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto_auto]">
            <div className="space-y-1">
              <Label htmlFor="ev-name">Name</Label>
              <Input id="ev-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Mobile Home Park Boot Camp, Dallas, April 2027" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ev-kind">Kind</Label>
              <Select id="ev-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
                {Object.entries(KIND_LABEL).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ev-brand">Brand</Label>
              <Select id="ev-brand" value={brand} disabled={editing} onChange={(e) => (setBrand(Number(e.target.value)), setProduct(""))}>
                {BRANDS.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.short}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="ev-loc">Where</Label>
              <Input id="ev-loc" value={location} onChange={(e) => setLocation(e.target.value)} placeholder={webinar ? "Online" : "Dallas, TX"} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="ev-zone">Time zone the dates are typed in</Label>
              <Select id="ev-zone" value={zone} onChange={(e) => setZone(e.target.value)}>
                {zones.map(([z, label]) => (
                  <option key={z} value={z}>
                    {label} ({z})
                  </option>
                ))}
              </Select>
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="ev-desc">Description (optional)</Label>
            <Textarea id="ev-desc" className="min-h-14" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="ev-status">Status</Label>
              <Select id="ev-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                {EVENT_STATUS.filter(([k]) => editing || k === "scheduled" || k === "draft").map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </Select>
              {editing && status === "cancelled" && event.status !== "cancelled" && (
                <p className="text-xs text-[var(--color-warning-fg)]">Cancelling the event doesn't cancel anyone's registration. Cancel those on the roster, or ask Claude to.</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="ev-product">Product that sells it (optional)</Label>
              <Select id="ev-product" value={product} onChange={(e) => setProduct(e.target.value ? Number(e.target.value) : "")}>
                <option value="">None</option>
                {(products.data?.products ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>
          {webinar && (
            <div className="space-y-1">
              <Label htmlFor="ev-demio">Demio event ID</Label>
              <Input id="ev-demio" className="h-8" value={demio} onChange={(e) => setDemio(e.target.value)} placeholder="From the event's settings in Demio" />
              <p className="text-xs text-[var(--color-muted-foreground)]">With it, everyone who registers gets their own Demio join link.</p>
            </div>
          )}

          <fieldset className="space-y-2 rounded-md border p-3">
            <legend className="px-1 text-xs font-semibold">Tags</legend>
            <TagRow label="Add when someone registers" help="So tag-based segments and older automations keep working.">
              <TagChips ids={registeredTags} onChange={setRegisteredTags} />
            </TagRow>
            <TagRow label="Add at check-in">
              <TagChips ids={attendedTags} onChange={setAttendedTags} />
            </TagRow>
            <TagRow label="Keap tags that mean registered" help="Anyone who gets one of these (including from Keap during the overlap) is put on the roster.">
              <TagChips ids={regTags} onChange={setRegTags} />
            </TagRow>
            <label className="flex items-center gap-2 pt-1">
              <Checkbox checked={noWaitlist} onChange={(e) => setNoWaitlist(e.target.checked)} /> Never waitlist: register people even past a date's seat limit
            </label>
          </fieldset>

          {!editing && (
            <fieldset className="space-y-2 rounded-md border p-3">
              <legend className="px-1 text-xs font-semibold">Dates</legend>
              <p className="text-xs text-[var(--color-muted-foreground)]">
                One row per class date or webinar time, in the time zone above. You can add more later on the event page.
              </p>
              {dates.map((d, i) => (
                <div key={i} className="grid gap-2 rounded-md bg-[var(--color-muted)]/40 p-2 sm:grid-cols-2">
                  <Input className="h-8" aria-label={`Date ${i + 1} name`} placeholder="Name (optional), e.g. April class" value={d.name} onChange={(e) => setDate(i, { name: e.target.value })} />
                  <div className="flex gap-2">
                    <Input className="h-8" aria-label={`Date ${i + 1} seats`} type="number" min={0} placeholder="Seats (blank = no limit)" value={d.capacity} onChange={(e) => setDate(i, { capacity: e.target.value })} />
                    <button aria-label={`Remove date ${i + 1}`} className="rounded p-1 hover:bg-[var(--color-muted)]" onClick={() => setDates(dates.filter((_, j) => j !== i))}>
                      <X className="size-4" />
                    </button>
                  </div>
                  <label className="space-y-0.5 text-xs text-[var(--color-muted-foreground)]">
                    Starts
                    <Input className="h-8" type="datetime-local" value={d.starts} onChange={(e) => setDate(i, { starts: e.target.value })} />
                  </label>
                  <label className="space-y-0.5 text-xs text-[var(--color-muted-foreground)]">
                    Ends (optional)
                    <Input className="h-8" type="datetime-local" value={d.ends} onChange={(e) => setDate(i, { ends: e.target.value })} />
                  </label>
                  <Input className="h-8" aria-label={`Date ${i + 1} place`} placeholder="Place, if different from the event's" value={d.location} onChange={(e) => setDate(i, { location: e.target.value })} />
                  {webinar && <Input className="h-8" aria-label={`Date ${i + 1} Demio date ID`} placeholder="Demio date ID" value={d.demio} onChange={(e) => setDate(i, { demio: e.target.value })} />}
                </div>
              ))}
              {badDates && <p className="text-xs text-[var(--color-danger-fg)]">A date ends before it starts.</p>}
              <button className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline" onClick={() => setDates([...dates, emptyDate()])}>
                <Plus className="size-3" /> Add a date
              </button>
            </fieldset>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending || !name.trim() || badDates} onClick={() => save.mutate()}>
            {editing ? "Save event" : "Create event"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Add one date to an event, or change one (time, place, seats, Demio ID, status). */
export function SessionDialog({ event, session, onClose }: { event: EventRecord; session?: SessionRecord; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const editing = !!session;
  const zone = event.time_zone || "America/Denver";
  const [name, setName] = useState(session?.name ?? "");
  const [starts, setStarts] = useState(toZoneLocalInput(session?.starts_at, zone));
  const [ends, setEnds] = useState(toZoneLocalInput(session?.ends_at, zone));
  const [location, setLocation] = useState(session?.location ?? "");
  const [capacity, setCapacity] = useState(session?.capacity ? String(session.capacity) : "");
  const [demio, setDemio] = useState(session?.demio_session_id ?? "");
  const [status, setStatus] = useState(session?.status ?? "scheduled");
  const startsChanged = editing && starts !== toZoneLocalInput(session.starts_at, zone);
  const zoneLabel = ZONES.find(([z]) => z === zone)?.[1] ?? zone;
  const save = useMutation({
    mutationFn: () =>
      editing
        ? api(`/api/v1/event-sessions/${session.id}`, {
            method: "PATCH",
            json: {
              name,
              ...(startsChanged ? { starts_at: starts } : {}),
              ...(ends !== toZoneLocalInput(session.ends_at, zone) ? { ends_at: ends } : {}),
              location,
              capacity: capacity ? Number(capacity) : 0,
              demio_session_id: demio,
              status,
            },
          })
        : api(`/api/v1/events/${event.id}/sessions`, {
            json: {
              name: name || undefined,
              starts_at: starts,
              ends_at: ends || undefined,
              location: location || undefined,
              capacity: capacity ? Number(capacity) : undefined,
              demio_session_id: demio || undefined,
            },
          }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["event", event.id] });
      void qc.invalidateQueries({ queryKey: ["roster", event.id] });
      void qc.invalidateQueries({ queryKey: ["events"] });
      toast(editing ? "Date saved" : "Date added");
      onClose();
    },
    onError: (e) => toast(e.message, "error"),
  });
  const bad = !!(ends && starts && ends < starts);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="w-[min(560px,calc(100vw-2rem))]">
        <DialogTitle>{editing ? "Edit date" : "Add a date"}</DialogTitle>
        <DialogDescription>
          Times are in {zoneLabel} time ({zone}), the event's time zone. The CRM shows them in Mountain Time.
        </DialogDescription>
        <div className="space-y-3 text-sm">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="se-name">Name (optional)</Label>
              <Input id="se-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="April class" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="se-cap">Seats</Label>
              <Input id="se-cap" type="number" min={0} value={capacity} onChange={(e) => setCapacity(e.target.value)} placeholder="Blank = no limit" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="se-start">Starts</Label>
              <Input id="se-start" type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="se-end">Ends (optional)</Label>
              <Input id="se-end" type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="se-loc">Place (optional)</Label>
              <Input id="se-loc" value={location} onChange={(e) => setLocation(e.target.value)} placeholder={event.location ?? ""} />
            </div>
            {(event.kind === "webinar" || demio) && (
              <div className="space-y-1">
                <Label htmlFor="se-demio">Demio date ID</Label>
                <Input id="se-demio" value={demio} onChange={(e) => setDemio(e.target.value)} />
              </div>
            )}
            {editing && (
              <div className="space-y-1">
                <Label htmlFor="se-status">Status</Label>
                <Select id="se-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="scheduled">Scheduled</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </div>
            )}
          </div>
          {bad && <p className="text-xs text-[var(--color-danger-fg)]">It ends before it starts.</p>}
          {startsChanged && !!session?.registered && (
            <p className="text-xs text-[var(--color-warning-fg)]">
              {session.registered} {session.registered === 1 ? "person is" : "people are"} registered. Reminders already scheduled keep the old time; ask Claude to move them to the new one.
            </p>
          )}
          {editing && status === "cancelled" && session.status !== "cancelled" && (
            <p className="text-xs text-[var(--color-warning-fg)]">Cancelling a date doesn't cancel its registrations or tell anyone. Move or cancel them on the roster.</p>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={save.isPending || !starts || bad} onClick={() => save.mutate()}>
            {editing ? "Save date" : "Add date"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TagRow({ label, help, children }: { label: string; help?: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[11rem_minmax(0,1fr)] items-start gap-3 max-sm:grid-cols-1 max-sm:gap-1">
      <div className="pt-1 text-xs text-[var(--color-muted-foreground)]">
        {label}
        {help && <div className="mt-0.5 text-[11px] leading-snug opacity-80">{help}</div>}
      </div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
